// src/services/remoteConfig.ts
// Mensajes para compartir la app, con el código de referido REAL del usuario.
//
// LO QUE ESTABA MAL:
//   1. La dirección de la tienda era una SIMULACIÓN con un `setTimeout` de
//      500 ms — un andamio de desarrollo que se quedó en producción. Encima
//      retrasaba medio segundo cada vez que alguien tocaba "Compartir".
//   2. El código de referido salía de un `userId = "USER789"` escrito a mano en
//      RewardsModal, así que TODOS los usuarios compartían el mismo código
//      (`REF-USER7`). Ningún referido se le podía acreditar a nadie: el bono por
//      invitar choferes era, literalmente, imposible de ganar.
//
// Ahora la dirección viene del servidor (configService → getStoreUrls), la misma
// fuente que usa el resto de la app, y el código sale del uid real.

import { configService } from './configService';

/** Código corto y legible a partir del uid. Estable: siempre el mismo por usuario. */
export function codigoReferido(uid: string): string {
  const limpio = String(uid || '').replace(/[^a-zA-Z0-9]/g, '');
  if (!limpio) return '';
  return `GS-${limpio.slice(0, 6).toUpperCase()}`;
}

export const RemoteConfigService = {
  /** Dirección oficial de la app en la tienda, según lo configurado en el servidor. */
  async getAppStoreUrl(): Promise<string> {
    try {
      const urls = await configService.getStoreUrls();
      return (
        urls?.playStoreUrl ||
        urls?.webUrl ||
        'https://play.google.com/store/apps/details?id=com.gosafely.rides'
      );
    } catch {
      // Si el servidor no responde, la dirección de Play se puede armar sola:
      // depende solo del nombre del paquete y no cambia nunca.
      return 'https://play.google.com/store/apps/details?id=com.gosafely.rides';
    }
  },

  /**
   * Mensaje para compartir, con el código de referido real de quien comparte.
   * `uid` debe ser el uid de Firebase del usuario — NUNCA un valor de ejemplo.
   *
   * `realCode` es el código REVERSIBLE de referral_codes (referralService.getMyCode).
   * Si se pasa, se usa ese (es el que el backend sabe canjear a un uid). Si no,
   * cae al codigoReferido(uid) legacy (solo display; no atribuye).
   *
   * El enlace lleva `?ref=CODE`: si el invitado ya tiene la app, el deep-link la
   * captura; si no, ve el código en el texto y lo escribe al registrarse.
   */
  async generateShareMessage(uid: string, userRole: string, realCode?: string): Promise<string> {
    const storeUrl = await this.getAppStoreUrl();
    // SOLO usamos el código reversible real (referral_codes). Si no lo tenemos
    // (fallo transitorio de red), NO caemos al codigoReferido legacy: ese código
    // NO existe en referral_codes, así que applyReferralCode lo rechazaría con
    // "código no existe" y el invitado vería un ERROR. Preferimos compartir SIN
    // código (no-atribución silenciosa, como dice la intención) a romperle el
    // registro al invitado.
    const codigo = realCode;
    const enlace = codigo
      ? `${storeUrl}${storeUrl.includes('?') ? '&' : '?'}ref=${encodeURIComponent(codigo)}`
      : storeUrl;

    if (userRole === 'RENT_CAR_OWNER') {
      const pitch = `Únete a mi flota en Go Safely y asegura tus ingresos.`;
      return codigo ? `${pitch}\n\nMi código: ${codigo}\n${enlace}` : `${pitch}\n\n${enlace}`;
    }
    if (userRole === 'DRIVER' || userRole === 'DELIVERY_DRIVER' || userRole === 'SCHOOL_DRIVER') {
      const pitch = `Trabaja con Go Safely y quédate con el 100% de lo que cobras: aquí no se paga comisión por viaje.`;
      return codigo
        ? `${pitch}\n\nEntra con mi código ${codigo} y ganamos los dos.\n${enlace}`
        : `${pitch}\n\n${enlace}`;
    }
    const pitch = `Pide tus viajes con Go Safely: tú propones el precio y el chofer decide. Invita amigos y ambos ganan un viaje GRATIS.`;
    return codigo
      ? `${pitch}\n\nUsa mi código ${codigo} al registrarte.\n${enlace}`
      : `${pitch}\n\n${enlace}`;
  },
};
